/**
 * Authentication API Service
 *
 * Serviço de autenticação com Python/FastAPI.
 * Usa JWT tokens para autenticação.
 *
 * @since 2025-12-15
 * @epic INIT-0045-03, INIT-0046
 */

import { api, isValidJwtFormat, saveTokens, clearTokens, getJwtToken, getRefreshToken } from './apiClient';
import { endpoints } from '@/config/backend';

// ==========================================
// TYPES
// ==========================================

export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface MFACredentials {
  code: string;
  sessionToken?: string; // Token temporário da primeira etapa
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  requireMFA?: boolean;
  mfaSessionToken?: string; // Token temporário para MFA
  user?: AuthUser;
  token?: string; // JWT token
  expiresAt?: string;
}

export interface AuthUser {
  id: number;
  username: string;
  email?: string;
  name: string;
  type?: 'individual' | 'corporation';
  roles?: string[];
  permissions?: string[];
  avatar?: string;
  lastLogin?: string;
  siteId?: number;
  idSite?: number; // Alias para compatibilidade
  /**
   * True se o usuário tem privilégios de superadmin.
   * O backend deriva da role admin/superadmin (ver `/auth/me`).
   * [TMF9-54372] Usado para esconder o menu "Configurações" e demais áreas
   * sensíveis no Sidebar.
   */
  isSuperuser?: boolean;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  password: string;
  passwordConfirm: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// ==========================================
// API FUNCTIONS
// ==========================================

/**
 * Login com username e password
 * Retorna JWT token
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const endpoint = endpoints.auth.login();

  try {
    const response = await api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
      user: AuthUser;
    }>(endpoint, {
      username: credentials.username,
      password: credentials.password,
    });

    // Armazenar JWT token (com validação)
    if (response.access_token && response.refresh_token) {
      saveTokens(response.access_token, response.refresh_token);
    }

    return {
      success: true,
      user: response.user,
      token: response.access_token,
      expiresAt: new Date(Date.now() + response.expires_in * 1000).toISOString(),
    };
  } catch (error: unknown) {
    // Extrair mensagem de erro do backend
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const axiosError = error as any;
    const detail = axiosError?.response?.data?.detail;

    // Pydantic 422 retorna detail como array de objetos [{loc, msg, type}]
    let backendMessage: string;
    if (Array.isArray(detail)) {
      backendMessage = detail.map((d: { msg?: string }) => d.msg || '').filter(Boolean).join('. ') || 'Erro de validação';
    } else {
      backendMessage =
        axiosError?.response?.data?.error?.message ||
        axiosError?.response?.data?.message ||
        (typeof detail === 'string' ? detail : null) ||
        'Erro ao fazer login';
    }

    return {
      success: false,
      message: backendMessage,
    };
  }
}

/**
 * Segunda etapa do login com MFA
 */
export async function loginMFA(credentials: MFACredentials): Promise<LoginResponse> {
  const endpoint = '/core/auth/mfa/verify';

  const response = await api.post<LoginResponse>(endpoint, {
    code: credentials.code,
    session_token: credentials.sessionToken,
  });

  // Nota: MFA retorna apenas access_token, não há refresh_token nesta etapa
  // O token será validado pelo interceptor ao ser usado
  if (response.token && isValidJwtFormat(response.token)) {
    localStorage.setItem('tmf_token', response.token);
  }

  return response;
}

/**
 * Logout
 * Remove token local e blacklist no servidor
 */
export async function logout(): Promise<void> {
  const endpoint = endpoints.auth.logout();

  try {
    await api.post(endpoint, { all_devices: false });
  } catch (error) {
    // Mesmo se falhar, limpa token local
    console.error('Erro no logout:', error);
  } finally {
    // Sempre limpa tokens locais
    clearTokens();
  }
}

/**
 * Obter dados do usuário atual.
 *
 * O backend retorna `/auth/me` em snake_case (Pydantic). Não há conversão
 * automática de case nesse endpoint, então mapeamos campos sensíveis
 * manualmente — em particular `is_superuser` → `isSuperuser` [TMF9-54372].
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const endpoint = endpoints.auth.me();

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(endpoint);
    if (!response) return null;

    const user: AuthUser = {
      ...(response as AuthUser),
      isSuperuser:
        response.isSuperuser ??
        response.is_superuser ??
        false,
    };
    return user;
  } catch (error) {
    // Se falhar (401, etc), retorna null
    return null;
  }
}

/**
 * Verificar se está autenticado
 */
export async function checkAuth(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}

/**
 * Refresh token
 */
export async function refreshAuth(): Promise<LoginResponse | null> {
  const endpoint = endpoints.auth.refresh();

  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const response = await api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    }>(endpoint, {
      refresh_token: refreshToken,
    });

    // Atualizar tokens (com validação)
    if (response.access_token && response.refresh_token) {
      saveTokens(response.access_token, response.refresh_token);
    }

    return {
      success: true,
      token: response.access_token,
      expiresAt: new Date(Date.now() + response.expires_in * 1000).toISOString(),
    };
  } catch (error) {
    clearTokens();
    return null;
  }
}

/**
 * Solicitar reset de senha
 */
export async function forgotPassword(data: ForgotPasswordData): Promise<ApiResponse<void>> {
  const endpoint = '/core/auth/password/forgot';
  return api.post<ApiResponse<void>>(endpoint, { email: data.email });
}

/**
 * Resetar senha com token
 */
export async function resetPassword(data: ResetPasswordData): Promise<ApiResponse<void>> {
  const endpoint = '/core/auth/password/reset';
  return api.post<ApiResponse<void>>(endpoint, {
    token: data.token,
    password: data.password,
    password_confirm: data.passwordConfirm,
  });
}

/**
 * Alterar senha do usuário logado
 */
export async function changePassword(data: ChangePasswordData): Promise<ApiResponse<void>> {
  const endpoint = '/core/auth/password/change';
  return api.post<ApiResponse<void>>(endpoint, {
    current_password: data.currentPassword,
    new_password: data.newPassword,
    new_password_confirm: data.newPasswordConfirm,
  });
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Verifica se há token JWT válido armazenado
 */
export function hasStoredToken(): boolean {
  return getJwtToken() !== null;
}

/**
 * Obter token JWT armazenado (retorna null se inválido)
 */
export function getStoredToken(): string | null {
  return getJwtToken();
}

/**
 * Limpar dados de autenticação
 */
export function clearAuthData(): void {
  clearTokens();
}

// ==========================================
// EXPORT
// ==========================================

export const authApi = {
  login,
  loginMFA,
  logout,
  getCurrentUser,
  checkAuth,
  refreshAuth,
  forgotPassword,
  resetPassword,
  changePassword,
  hasStoredToken,
  getStoredToken,
  clearAuthData,
};

export default authApi;
